import CustomToast from "../../utils/functions/CustomToast";
import HttpClient from "../../utils/functions/HttpClient";
import ServerEndpoints from "../../utils/constants/ServerEndpoints";
import Errors from "../../utils/constants/Errors";

const request = async (operation, { setApiError, setIsLoading } = {}) => {
  setApiError?.(false);

  try {
    return await operation();
  } catch (err) {
    setApiError?.(true);
    handleAppointmentRequestError(err);
    throw err;
  } finally {
    setIsLoading?.(false);
  }
};

const normalizeAppointmentQueryParams = (params = {}) => {
  const normalizedParams = { ...params };

  if (normalizedParams.pageSize != null && normalizedParams.size == null) {
    normalizedParams.size = normalizedParams.pageSize;
  }

  delete normalizedParams.pageSize;

  return normalizedParams;
};

export const getAllAppointments = async (
  setAppointmentsData,
  params,
  setApiError,
  setIsLoading
) => {
  const normalizedParams = normalizeAppointmentQueryParams(params);
  const response = await request(
    () =>
      HttpClient.getAxiosClient().get(`${ServerEndpoints.ADMIN_APPOINTMENTS_ENDPOINT}`, {
        params: normalizedParams,
      }),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    setAppointmentsData(response.data);
  }
};

export const getAppointmentById = async (
  setAppointmentData,
  id,
  setApiError,
  setIsLoading
) => {
  const response = await request(
    () => HttpClient.getAxiosClient().get(`${ServerEndpoints.ADMIN_APPOINTMENTS_ENDPOINT}/${id}`),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    setAppointmentData(response.data);
  }
};

export const deleteAppointment = async (id, setState, setApiError, setIsLoading) => {
  const response = await request(
    () => HttpClient.getAxiosClient().delete(`${ServerEndpoints.APPOINTMENTS_ENDPOINT}/${id}`),
    { setApiError, setIsLoading }
  );

  if (response.status === 204) {
    CustomToast("Appointment successfully deleted", "success");
    setState?.((prev) => !prev);
  }
};

export const deleteAppointments = async (ids, setState, setApiError, setIsLoading) => {
  const response = await request(
    async () => {
      await Promise.all(
        ids.map((id) =>
          HttpClient.getAxiosClient().delete(`${ServerEndpoints.APPOINTMENTS_ENDPOINT}/${id}`)
        )
      );

      return { status: 204 };
    },
    { setApiError, setIsLoading }
  );

  if (response.status === 204) {
    CustomToast("Appointments successfully deleted", "success");
    setState?.((prev) => !prev);
  }
};

export const cancelAppointment = async (
  id,
  setState,
  setApiError,
  setIsLoading
) => {
  const response = await request(
    () => HttpClient.getAxiosClient().patch(`${ServerEndpoints.APPOINTMENTS_ENDPOINT}/${id}/cancel`),
    { setApiError, setIsLoading }
  );

  if (response.status === 204) {
    CustomToast("Appointment successfully canceled", "success");
    setState?.((prev) => !prev);
  }
};

export const restoreAppointment = async (
  id,
  setState,
  setApiError,
  setIsLoading
) => {
  const response = await request(
    () => HttpClient.getAxiosClient().put(`${ServerEndpoints.ADMIN_APPOINTMENTS_ENDPOINT}/${id}/restore`),
    { setApiError, setIsLoading }
  );

  if (response.status === 200 || response.status === 204) {
    CustomToast("Appointment successfully restored", "success");
    setState?.((prev) => !prev);
  }

  return response.data;
};

export const updateAppointment = async (
  appointment,
  setApiError,
  setState,
  setIsLoading
) => {
  const response = await request(
    () =>
      HttpClient.getAxiosClient().put(
        `${ServerEndpoints.APPOINTMENTS_ENDPOINT}/${appointment.id}`,
        appointment
      ),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    CustomToast("Appointment successfully updated", "success");
    setState?.((prev) => !prev);
  }

  return response.data;
};

export const createAppointment = async (
  appointment,
  setApiError,
  setState,
  setIsLoading
) => {
  const response = await request(
    () => HttpClient.getAxiosClient().post(`${ServerEndpoints.ADMIN_APPOINTMENTS_ENDPOINT}`, appointment),
    { setApiError, setIsLoading }
  );

  if (response.status === 201 || response.status === 200) {
    CustomToast("Appointment successfully created", "success");
    setState?.((prev) => !prev);
  }

  return response.data;
};

const getErrorStatus = (err) => err?.response?.status;

const getBackendErrorMessage = (err) => {
  const data = err?.response?.data;

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors[0];
  }

  return data?.message || null;
};

const handleAppointmentRequestError = (err) => {
  const backendMessage = getBackendErrorMessage(err);

  switch (getErrorStatus(err)) {
    case 400:
      CustomToast(backendMessage || "Please review your data and try again.", "error");
      return;
    case 401:
      CustomToast("Please sign in to continue.", "error");
      return;
    case 403:
      CustomToast(backendMessage || "You are not allowed to perform this action.", "error");
      return;
    case 404:
      CustomToast(backendMessage || "The appointment could not be found.", "error");
      return;
    case 409:
      CustomToast(backendMessage || "The appointment time frame is no longer available.", "error");
      return;
    case 503:
      CustomToast(Errors.SERVICE_UNAVALAIBLE_ERROR, "error");
      return;
    default:
      showGenericRequestError(err);
  }
};

const showGenericRequestError = (err) => {
  if (err?.response == null) {
    CustomToast(Errors.API_ERROR, "error");
    return;
  }

  const backendMessage = getBackendErrorMessage(err);
  CustomToast(backendMessage || Errors.API_ERROR, "error");
};
